import { action, computed, observable, runInAction } from 'mobx';
import {
  BaseAvailabilityStatus,
  BaseConfirmationType,
  ProductLayoutType,
  type ActivityPlan,
  type CurrencyCurrency,
  type LanguageCode,
  type OptionProduct,
  type Product
} from '@linktivity/ars-neo';
import {
  startingPrice,
  type CapacityLimits,
  type StartingPrice
} from '@link/share/booking';
import type { PlanDetail, UnitCardUnit } from '@link/share/business';
import type { SelectedOption } from '@link/share/business/OptionCard';
import {
  getPlanDateFallback,
  updatePlanState,
  type PlanAutoSwitchConfirm,
  type PlanDateFallback
} from '@link/share/hooks/planState';
import {
  buildCollapsiblePlans,
  buildPaymentData,
  computeMaxCapacity
} from '@link/share/hooks/productPageSelectors';
import {
  convertDateToString,
  getAffiliateId,
  getDependentUnitWarningTitle,
  getOptionUnitRestrictionResult,
  isDefined,
  isOpenDatePlan,
  isOptionSoldOutOnDate,
  isOptionUnitRatioActive,
  resolveStartTimeById,
  resolveStartTimeValue,
  resolveValidityExpirationDate,
  sumQuantities,
  sumSelectedOptionWeightedQuantities
} from '@link/share/utils';
import { trackGa4AddToCart, trackGa4ViewItem } from '@link/share/vendors/ga4';
import { bookingApi } from '../api';
import {
  activationDate,
  columnPlanForDate,
  isDayBookableForAnyPlan,
  isPlanBookableOnDate,
  type CalendarView
} from '../state/bookability';
import type { CalendarDay } from '../state/calendarRange';
import {
  applyOptionAvailability,
  coveredUntil,
  findOptionSoldOutDates,
  hasSameDayLinkedOption,
  markUnservedSlots,
  type MainBookingContext,
  type OptionCalendars
} from '../state/optionAvailability';
import { pickFitsMainBooking, toOptionForms } from '../state/optionSelection';
import {
  lotteryPhase,
  orderBlockers,
  submitLabelKey
} from '../state/orderGate';
import {
  landPlanOnDate,
  pickDate,
  repriceUnits,
  seedFirstDates,
  type PlanStates
} from '../state/planMoves';
import { buildSession } from '../state/session';
import { settle } from './autoMoves';
import { CalendarStore } from './calendar';

/** Selection facts that are not rendered, so not observable. */
export interface SelectionMemory {
  /**
   * The date the visitor owns: one they picked, or one a plan switch landed
   * on (and announced, if it moved). '' until then; meanwhile a switch
   * carries over the date the active plan shows.
   */
  ownedDate: string;
  /** The column layout's opening selection has been made. */
  columnInitialized: boolean;
}

export interface BookingStoreOptions {
  /** The route's product, from the loader. */
  product: Product;
  /** The page's locale code, e.g. `ja`. */
  locale: string;
  languageCode: LanguageCode;
  currency: CurrencyCurrency;
  showOtherCurrencies: boolean;
  /** From `AppCapabilities.columnPlanAutoSwitch`. */
  enableColumnPlanAutoSwitch: boolean;
}

const EMPTY_OPTION_CALENDARS: OptionCalendars = new Map();

/**
 * The booking half of the product page: plan, date, time slot, units and
 * options, and the session they submit.
 *
 * Replaces share's `useProductPage` for this app. Its rules are kept — the
 * judging lives in `../state/`, carried over function by function — and its
 * shape is not:
 *
 * - One store per page, created with the page (`useState`), never shared. The
 *   SPA's trouble with MobX was module-level singletons, which a server would
 *   share across visitors; an instance per page has nothing to share.
 * - The product is the loader's. Availability is fetched in the browser, by
 *   CalendarStore.
 * - Derived data are `@computed`, so nothing tracks dependencies by hand.
 * - Each thing that happens — the calendar arriving, a date picked, a plan
 *   opened — is one `@action`. The SPA hook let effects watch derived state
 *   and correct each other, in an order only comments held together; here the
 *   automatic moves run in `settle()`, in a written order, after every
 *   change.
 * - No paths, no navigation, no messages. `book()` resolves with the session
 *   id, and failures are thrown for the page to show.
 */
export class BookingStore {
  readonly product: Product;
  readonly plans: ActivityPlan[];
  readonly optionProducts: OptionProduct[];
  /** Options must be picked, and the product has some. */
  readonly needsOptionCalendars: boolean;
  readonly isLottery: boolean;
  /**
   * The column layout's cross-plan inventory: one calendar for every plan,
   * opening on the first day any plan can serve, and moving to the leftmost
   * plan with stock when the chosen day sells out for the active one.
   */
  readonly isColumnPlanAutoSwitch: boolean;
  readonly hasConfirmationPopup: boolean;
  readonly currency: CurrencyCurrency;
  readonly showOtherCurrencies: boolean;
  private readonly options: BookingStoreOptions;

  /** Availability: the calendars, and loading them. */
  readonly calendar: CalendarStore;

  // --- Selection ---
  @observable accessor activePlanId: string | undefined = undefined;
  @observable.ref accessor planStates: PlanStates = new Map();
  /**
   * Options picked in the editor, as picked. What the booking takes is
   * `selectedOptions`: those that still fit the main booking.
   */
  @observable.ref accessor pickedOptions: SelectedOption[] = [];
  /** What the selection remembers but never renders; see SelectionMemory. */
  readonly memory: SelectionMemory = {
    ownedDate: '',
    columnInitialized: false
  };

  // --- Notices and dialogs ---
  /**
   * The plan the column layout moved off because the chosen day sold out on
   * it; '' while the active plan is the visitor's own choice.
   */
  @observable accessor autoSwitchedFromPlanId = '';
  /** A date on screen was moved; shown until dismissed. */
  @observable.ref accessor planDateFallback: PlanDateFallback | null = null;
  /** A picked date was handed to another plan; shown until dismissed. */
  @observable.ref accessor planAutoSwitchConfirm: PlanAutoSwitchConfirm | null =
    null;
  @observable accessor orderConfirmVisible = false;
  @observable accessor bookingLoading = false;
  @observable accessor detailPlanId: string | undefined = undefined;
  @observable accessor planDetailVisible = false;

  /**
   * When the page started in the browser; null on the server and in the
   * render that hydrates. Rules that depend on today's date read this rather
   * than `new Date()`: the server's clock is in UTC and its HTML is cached,
   * so a date-dependent label there would disagree with the browser's.
   */
  @observable.ref accessor now: Date | null = null;

  constructor(options: BookingStoreOptions) {
    this.options = options;
    const { product } = options;
    this.product = product;
    this.plans = product.productContent?.activity?.plans ?? [];
    // Same filter as `deriveProductDisplayInfo`.
    this.optionProducts =
      product.optionProducts
        ?.filter(isDefined)
        .filter(option => option.productContent != null) ?? [];
    this.needsOptionCalendars =
      (product.optionRequired ?? false) && this.optionProducts.length > 0;
    this.isLottery =
      product.confirmationType === BaseConfirmationType.ConfirmationTypeLottery;
    this.isColumnPlanAutoSwitch =
      options.enableColumnPlanAutoSwitch &&
      !this.isLottery &&
      product.layout?.layoutType === ProductLayoutType.ProductLayoutTypeColumn;
    this.hasConfirmationPopup = isDefined(product.bookingConfirmationPopup);
    this.currency = options.currency;
    this.showOtherCurrencies = options.showOtherCurrencies;
    this.calendar = new CalendarStore({
      product,
      languageCode: options.languageCode,
      currency: options.currency,
      requiredOptions: this.needsOptionCalendars ? this.optionProducts : [],
      optionProducts: this.optionProducts
    });

    // The first plan opens with the page.
    const firstPlanId = this.plans[0]?.id;
    this.activePlanId = firstPlanId;
    if (firstPlanId) {
      this.planStates = updatePlanState(new Map(), firstPlanId, {});
    }
  }

  // ===== Derived =====

  @computed get activePlan() {
    return this.plans.find(plan => plan.id === this.activePlanId);
  }

  @computed get activePlanState() {
    return this.activePlanId
      ? this.planStates.get(this.activePlanId)
      : undefined;
  }

  @computed get mainIsOpenDate() {
    return isOpenDatePlan(this.activePlan);
  }

  @computed get mainValidityExpiration() {
    return this.mainIsOpenDate
      ? resolveValidityExpirationDate(
          this.activePlan?.validityPeriod,
          this.now ?? undefined
        )
      : '';
  }

  /** Canonical start time: by slot id, else the stored time. Never the label. */
  @computed get mainStartTime() {
    return (
      resolveStartTimeById(
        this.activePlan?.startTimes,
        this.activePlanState?.selectedTimeId
      ) ||
      (this.activePlanState?.selectedTime ?? '')
    );
  }

  @computed get mainProductQuantity() {
    return sumQuantities(
      this.activePlanState?.selectedUnits.map(unit => unit.quantity) ?? []
    );
  }

  @computed get optionContext(): MainBookingContext {
    const optionCalendars =
      (this.needsOptionCalendars && this.calendar.optionCalendars) ||
      EMPTY_OPTION_CALENDARS;
    return {
      optionProducts: this.optionProducts,
      optionCalendars,
      coveredUntil: coveredUntil(optionCalendars),
      mainIsOpenDate: this.mainIsOpenDate,
      mainValidityExpiration: this.mainValidityExpiration,
      today: this.today
    };
  }

  @computed get optionSoldOutDates() {
    return findOptionSoldOutDates(this.calendar.days, this.optionContext);
  }

  /**
   * The dates whose slots the calendar shows — each plan's selected date —
   * when slots need judging against same-day-linked options; null otherwise.
   */
  @computed get slotDates(): Set<string> | null {
    if (!hasSameDayLinkedOption(this.optionContext)) return null;
    const dates = new Set<string>();
    for (const state of this.planStates.values()) {
      if (state.selectedDate) dates.add(state.selectedDate);
    }
    return dates;
  }

  /** The days as the calendar renders them. */
  @computed get calendarDays() {
    return applyOptionAvailability(
      this.calendar.days,
      this.optionSoldOutDates,
      this.slotDates,
      this.optionContext
    );
  }

  @computed get view(): CalendarView {
    const slotDates = this.slotDates;
    const context = this.optionContext;
    return {
      plans: this.plans,
      days: this.calendarDays,
      markSlots: slotDates
        ? day =>
            day.date && slotDates.has(day.date)
              ? day
              : markUnservedSlots(day, context)
        : day => day
    };
  }

  /** Column layout only; undefined otherwise, as the plan views expect. */
  @computed get isDayBookableForAnyPlan() {
    const view = this.view;
    return this.isColumnPlanAutoSwitch
      ? (day?: CalendarDay) => isDayBookableForAnyPlan(view, day)
      : undefined;
  }

  /** Column layout only; undefined otherwise, as the plan views expect. */
  @computed get isPlanBookableOnDate() {
    const view = this.view;
    return this.isColumnPlanAutoSwitch
      ? (planId: string, date: string) =>
          isPlanBookableOnDate(view, planId, date)
      : undefined;
  }

  /** The units the active plan sells on its selected day. */
  /**
   * The active plan's capacity limits for the selected slot: its maximum,
   * lowered to the slot's remaining allotment, as in the SPA.
   */
  @computed get capacity(): CapacityLimits {
    const date = this.activePlanState?.selectedDate;
    const clamped = computeMaxCapacity({
      activePlanId: this.activePlanId,
      activePlan: this.activePlan,
      selectedTimeId: this.activePlanState?.selectedTimeId,
      timeAvailabilities: this.calendar.days.find(day => day.date === date)
        ?.timeAvailabilities
    });
    return {
      // 0 is both "no cap" and "none left"; a slot with none left cannot be
      // selected, so it is read as no cap.
      max: clamped > 0 ? clamped : undefined,
      min: this.activePlan?.minCapacityInclusive
    };
  }

  @computed get dayUnits() {
    const date = this.activePlanState?.selectedDate;
    const day = this.calendar.days.find(item => item.date === date);
    return (
      day?.planInfos?.find(
        info =>
          info?.planId === this.activePlanId &&
          info?.status === BaseAvailabilityStatus.AvailabilityStatusOk
      )?.unitItems ?? []
    );
  }

  @computed get dependentUnitWarningTitle() {
    return getDependentUnitWarningTitle(
      this.activePlanState?.selectedUnits ?? []
    );
  }

  get unitRestriction() {
    return this.product.optionRule?.unitRestriction;
  }

  @computed get optionUnitRestrictionResult() {
    return getOptionUnitRestrictionResult({
      // Products with no ratio don't count toward the rule, so with none of
      // them weighted there is nothing to compare.
      restriction: this.optionProducts.some(option =>
        isOptionUnitRatioActive(option.unitRatio)
      )
        ? this.unitRestriction
        : undefined,
      mainQuantity: this.mainProductQuantity,
      optionQuantity: sumSelectedOptionWeightedQuantities(
        this.optionProducts,
        this.selectedOptions
      )
    });
  }

  @computed get missingRequiredOption() {
    return (
      this.needsOptionCalendars &&
      !this.selectedOptions.some(
        option => (option.displayInfo?.totalQuantity ?? 0) > 0
      )
    );
  }

  /**
   * YYYY-MM-DD. Before the page is in the browser there is no availability to
   * judge with it, so the server's clock never reaches the HTML.
   */
  get today() {
    return convertDateToString(this.now ?? new Date());
  }

  /** Unknown until the page is in the browser; see `now`. */
  @computed get lotteryPhase() {
    return this.now ? lotteryPhase(this.product, this.now) : undefined;
  }

  @computed get submitLabelKey() {
    return submitLabelKey(this.isLottery, this.lotteryPhase);
  }

  @computed get orderBlockers() {
    return orderBlockers({
      activePlan: this.activePlan,
      activePlanState: this.activePlanState,
      dayUnits: this.dayUnits,
      maxCapacity: this.capacity.max,
      dependentUnitWarning: !!this.dependentUnitWarningTitle,
      lotteryPhase: this.lotteryPhase,
      missingRequiredOption: this.missingRequiredOption,
      optionUnitRestriction: this.optionUnitRestrictionResult,
      optionSoldOutDates: this.optionSoldOutDates
    });
  }

  @computed get orderDisabled() {
    return this.orderBlockers.length > 0;
  }

  @computed get paymentData() {
    return buildPaymentData({
      activePlanId: this.activePlanId,
      activePlanState: this.activePlanState,
      plans: this.plans,
      unitItemsMap: new Map(
        this.dayUnits.map(unit => [
          unit.id,
          {
            id: unit.id,
            amount: unit.amount,
            displayAmount: unit.displayAmount
          }
        ])
      ),
      selectedOptions: this.selectedOptions,
      optionProducts: this.optionProducts,
      showOtherCurrencies: this.options.showOtherCurrencies,
      today: this.now
    });
  }

  /** The plan cards' data, as share's plan views take it. */
  @computed get collapsiblePlans() {
    return buildCollapsiblePlans({
      plans: this.plans,
      dailyAvailabilities: this.calendarDays,
      planStates: this.planStates,
      cancellationPolicy: this.cancellationPolicy,
      currency: this.options.currency,
      showOtherCurrencies: this.options.showOtherCurrencies,
      confirmationType: this.product.confirmationType
    });
  }

  /** The picked options the main booking still allows; see pickFitsMainBooking. */
  @computed get selectedOptions(): SelectedOption[] {
    const mainDate = this.activePlanState?.selectedDate ?? '';
    return this.pickedOptions.filter(pick =>
      pickFitsMainBooking(
        pick,
        this.optionProductById.get(pick.productContentId),
        mainDate,
        this.mainStartTime
      )
    );
  }

  @computed get optionForms() {
    return toOptionForms(this.selectedOptions);
  }

  @computed get optionProductById() {
    return new Map(
      this.optionProducts.map(option => [
        option.productContent?.id ?? '',
        option
      ])
    );
  }

  /**
   * Options with no stock on any date the main booking allows them. Unknown
   * until their calendars load, and never for one whose calendar failed.
   */
  @computed get soldOutOptionIds(): Set<string> {
    const context = {
      mainTargetDate: this.activePlanState?.selectedDate ?? '',
      mainIsOpenDate: this.mainIsOpenDate,
      mainValidityExpiration: this.mainValidityExpiration,
      today: this.today
    };
    const soldOut = new Set<string>();
    for (const [id, calendar] of this.calendar.optionProductCalendars) {
      const option = this.optionProductById.get(id);
      if (
        isOptionSoldOutOnDate(
          option?.timeLinkage,
          calendar.dailyAvailabilities ?? [],
          context
        )
      ) {
        soldOut.add(id);
      }
    }
    return soldOut;
  }

  /**
   * Each plan's "from" price. Priced by the calendar once it has the plan's
   * units, and by the product's own units until then — so the server renders
   * a price too, and the card may change it once the calendar arrives.
   */
  @computed get startingPrices(): Map<string, StartingPrice | undefined> {
    return new Map(
      this.collapsiblePlans.map(({ activityPlan, units }) => [
        activityPlan.id ?? '',
        startingPrice(
          units.length ? units : (activityPlan.unitItems ?? []),
          this.showOtherCurrencies
        )
      ])
    );
  }

  get cancellationPolicy() {
    return (
      this.product.productContent?.activity?.cancelPolicy?.description ?? ''
    );
  }

  @computed get selectedPlanDetail(): PlanDetail | null {
    const plan = this.plans.find(item => item.id === this.detailPlanId);
    return plan
      ? { ...plan, cancellationPolicy: this.cancellationPolicy }
      : null;
  }

  // ===== Data arriving =====

  /**
   * Load the first months; then seed each plan's date. Throws on failure.
   * Called once the page is in the browser, so it also starts the clock.
   */
  async loadCalendar() {
    if (!this.now) runInAction(() => (this.now = new Date()));
    if (await this.calendar.loadInitial()) this.calendarLoaded();
  }

  /**
   * Keep `now` on today's date while the page stays open: at local midnight,
   * and when the tab comes back (timers stall in a background tab or a
   * sleeping laptop). Browser only. Returns the cleanup.
   */
  startClock(): () => void {
    let timer: ReturnType<typeof setTimeout>;
    const scheduleMidnight = () => {
      clearTimeout(timer);
      const now = new Date();
      const midnight = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1
      );
      timer = setTimeout(
        () => {
          this.tick();
          scheduleMidnight();
        },
        midnight.getTime() - now.getTime() + 1000
      );
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      this.tick();
      scheduleMidnight();
    };
    scheduleMidnight();
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }

  /** Move `now` on once the date has changed; a no-op before the clock starts. */
  @action.bound
  private tick() {
    if (!this.now) return;
    const now = new Date();
    if (convertDateToString(now) !== convertDateToString(this.now)) {
      this.now = now;
    }
  }

  @action private calendarLoaded() {
    this.planStates = seedFirstDates(
      this.planStates,
      this.plans,
      this.calendar.days
    );
    settle(this);
  }

  /** Load the next months, when the calendar asks. Throws on failure. */
  @action.bound
  async loadMoreCalendar() {
    if (await this.calendar.loadMore()) runInAction(() => settle(this));
  }

  /**
   * Load the options' calendars: the required ones to judge main dates
   * against, and all of them to show which are sold out.
   */
  async loadOptionCalendars() {
    // In parallel, but the selection is corrected as soon as the required
    // ones arrive, without waiting for the rest.
    const all = this.calendar.loadOptionProductCalendars();
    if (await this.calendar.loadOptionCalendars()) {
      runInAction(() => settle(this));
    }
    await all;
  }

  // ===== The visitor =====

  /** A date picked on a plan's calendar. */
  @action.bound
  selectDate(planId: string, date: string) {
    this.memory.ownedDate = date;
    // On the column calendar the date decides the plan: the leftmost one with
    // stock on it.
    const replacementPlanId = this.isColumnPlanAutoSwitch
      ? columnPlanForDate(this.view, planId, date)
      : '';
    const targetPlanId = replacementPlanId || planId;
    if (this.isColumnPlanAutoSwitch) {
      // Only a plan that sold out on the date leaves something to explain;
      // moving left off one that still has stock is just the preference.
      this.autoSwitchedFromPlanId =
        replacementPlanId && !isPlanBookableOnDate(this.view, planId, date)
          ? planId
          : '';
    }
    const replacementTitle = replacementPlanId
      ? (this.plans.find(plan => plan.id === replacementPlanId)?.title ?? '')
      : '';
    if (replacementTitle) {
      this.planAutoSwitchConfirm = { date, planTitle: replacementTitle };
    }
    if (replacementPlanId) this.activePlanId = replacementPlanId;
    this.planStates = pickDate(
      this.planStates,
      targetPlanId,
      date,
      !!replacementPlanId,
      this.calendar.days
    );
    settle(this);
  }

  /** A plan card opened or closed. */
  @action.bound
  expandPlan(planId: string, expanded: boolean) {
    if (expanded) this.activatePlan(planId);
    else this.activePlanId = undefined;
    settle(this);
  }

  /**
   * Make a plan the active one: raise a notice if its date moves, take that
   * date as owned so later switches follow it, then land the plan on it.
   */
  private activatePlan(planId: string) {
    // A choice of the visitor's; the column layout's opening selection must
    // not run after it and overwrite it.
    if (this.isColumnPlanAutoSwitch) this.memory.columnInitialized = true;
    // Before the visitor has picked a date, the one carried over is the date
    // the active plan shows. An open-date plan's is never on screen.
    const sourceDate =
      this.memory.ownedDate ||
      (isOpenDatePlan(this.activePlan)
        ? ''
        : (this.activePlanState?.selectedDate ?? ''));
    const date = activationDate(this.view, planId, sourceDate, {
      isLottery: this.isLottery,
      isColumn: this.isColumnPlanAutoSwitch
    });
    // Raises a notice, never clears one: one raised by an automatic move may
    // not have been read yet.
    const fallback = getPlanDateFallback(sourceDate, date);
    if (fallback) this.planDateFallback = fallback;
    if (date) this.memory.ownedDate = date;
    this.autoSwitchedFromPlanId = '';
    this.activePlanId = planId;
    // The column layout has one slot row for every plan, so the incoming plan
    // starts without a slot even on the same date.
    this.planStates = landPlanOnDate(
      this.planStates,
      planId,
      date,
      this.isColumnPlanAutoSwitch,
      this.calendar.days
    );
  }

  @action.bound
  selectTime(planId: string, timeId: string) {
    // The label as the plan card shows it.
    const label =
      this.collapsiblePlans
        .find(plan => plan.activityPlan.id === planId)
        ?.timeSlots?.find(slot => slot.value === timeId)?.label ?? '';
    const startTime = timeId
      ? this.plans
          .find(plan => plan.id === planId)
          ?.startTimes?.find(st => st.id === timeId)
      : undefined;
    this.planStates = updatePlanState(this.planStates, planId, {
      selectedTimeId: timeId,
      selectedTimeTitle: label,
      selectedTime: resolveStartTimeValue(startTime)
    });
    settle(this);
  }

  @action.bound
  changeUnits(planId: string, units: UnitCardUnit[]) {
    this.planStates = updatePlanState(this.planStates, planId, {
      selectedUnits: units
    });
  }

  /** An option added or changed in the editor; replaces any earlier pick. */
  @action.bound
  pickOption(pick: SelectedOption) {
    const id = pick.productContentId;
    this.pickedOptions = this.pickedOptions.some(
      item => item.productContentId === id
    )
      ? this.pickedOptions.map(item =>
          item.productContentId === id ? pick : item
        )
      : [...this.pickedOptions, pick];
  }

  @action.bound
  removeOption(productContentId: string) {
    this.pickedOptions = this.pickedOptions.filter(
      item => item.productContentId !== productContentId
    );
  }

  @action.bound
  dismissPlanDateFallback() {
    this.planDateFallback = null;
  }

  @action.bound
  dismissPlanAutoSwitchConfirm() {
    this.planAutoSwitchConfirm = null;
  }

  @action.bound
  showPlanDetail(plan: ActivityPlan) {
    if (!plan.id) return;
    this.detailPlanId = plan.id;
    this.planDetailVisible = true;
  }

  @action.bound
  closePlanDetail() {
    this.planDetailVisible = false;
  }

  @action.bound
  setOrderConfirmVisible(visible: boolean) {
    this.orderConfirmVisible = visible;
  }

  private viewTracked = false;

  /** view_item, once per store, when the page is on screen. */
  trackView() {
    if (this.viewTracked) return;
    this.viewTracked = true;
    if (this.product.productContent?.activity?.id) {
      trackGa4ViewItem(this.product, this.options.locale);
    }
  }

  /**
   * Send the session. Resolves with its id, or undefined when there was
   * nothing to send. Throws when the request fails.
   */
  async book(): Promise<string | undefined> {
    // The button reflects the date it last rendered with; a lottery may have
    // opened or closed since.
    this.tick();
    if (this.orderDisabled) return undefined;
    const state = this.activePlanState;
    if (!state) return undefined;
    const session = buildSession({
      product: this.product,
      languageCode: this.options.languageCode,
      currency: this.options.currency,
      planState: state,
      pricedUnits: repriceUnits(
        state.selectedUnits,
        state.planId,
        state.selectedDate,
        this.calendar.days
      ),
      optionForms: this.optionForms,
      affiliateId: getAffiliateId()
    });
    if (!session) return undefined;
    runInAction(() => (this.bookingLoading = true));
    try {
      const res = await bookingApi.putSession({ body: { session } });
      const created = res?.session;
      if (created) {
        // From the PUT response: the session the backend actually stored.
        const activity = this.product.productContent?.activity;
        trackGa4AddToCart(created, {
          uiLanguage: this.options.locale,
          currency: this.options.currency,
          showOtherCurrencies: this.options.showOtherCurrencies,
          fallback: {
            agentId: this.product.agentId,
            activityId: activity?.id,
            activityTitle: activity?.title
          }
        });
      }
      return created?.id;
    } finally {
      runInAction(() => {
        this.bookingLoading = false;
        this.orderConfirmVisible = false;
      });
    }
  }
}
